import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, LineChart, Line, YAxis, ReferenceLine, Tooltip } from "recharts";
import { Activity } from "lucide-react";
import { api, fmtUSD } from "@/lib/api";

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const TipBox = ({ active, payload }) => active && payload?.length ? (
  <div className="bg-slate-950 border border-slate-800 px-2 py-1 text-[10px] font-mono-num">
    <div className="text-zinc-500">{payload[0].payload.time} · {payload[0].payload.symbol}</div>
    <div className="text-zinc-100">${payload[0].value.toFixed(2)}</div>
  </div>
) : null;

export default function LiveTicker() {
  const date = todayStr();
  const { data } = useQuery({
    queryKey: ["live-ticker", date],
    queryFn: async () => (await api.get(`/calendar/${date}`)).data,
    refetchInterval: 30_000,
    retry: false,
  });

  const curve = useMemo(() => data?.equity_curve || [], [data]);

  // Hide entirely when no trades today
  if (!curve || curve.length === 0) return null;

  const pnl = data.total_pnl;
  const isUp = pnl >= 0;
  const color = isUp ? "#34d399" : "#fb7185";
  const glow = isUp ? "rgba(52,211,153,0.55)" : "rgba(251,113,133,0.55)";
  const trades = data.trades;
  const best = data.best_trade;
  const worst = data.worst_trade;

  return (
    <div
      className="flex items-stretch gap-4 px-6 py-2.5 border-b border-slate-800/70 bg-slate-950/60 backdrop-blur-md"
      data-testid="live-ticker"
    >
      {/* Pulsing live dot */}
      <div className="flex items-center gap-2 shrink-0">
        <span className="relative flex h-2 w-2">
          <span
            className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping"
            style={{ background: color }}
          />
          <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: color, boxShadow: `0 0 10px ${glow}` }} />
        </span>
        <span className="label-eyebrow text-zinc-300 tracking-[0.25em]">LIVE · TODAY</span>
      </div>

      {/* Metrics */}
      <div className="flex items-center gap-5 shrink-0 font-mono-num text-xs">
        <div>
          <span className="text-zinc-500">P&L </span>
          <span style={{ color }} className="font-medium tabular-nums" data-testid="live-pnl">
            {isUp ? "+" : ""}{fmtUSD(pnl)}
          </span>
        </div>
        <div className="text-zinc-500 hidden md:block">
          TRADES <span className="text-zinc-200">{trades}</span>
        </div>
        <div className="text-zinc-500 hidden lg:block">
          BEST <span className="text-emerald-400">{fmtUSD(best)}</span>
        </div>
        <div className="text-zinc-500 hidden lg:block">
          WORST <span className="text-rose-400">{fmtUSD(worst)}</span>
        </div>
      </div>

      {/* Animated sparkline */}
      <div className="flex-1 min-w-0 h-7 -my-0.5 relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-12 z-10 pointer-events-none"
             style={{ background: "linear-gradient(to right, hsl(222 45% 8% / 0.95), transparent)" }} />
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={curve} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <ReferenceLine y={0} stroke="#3f3f46" strokeDasharray="2 4" strokeOpacity={0.5} />
            <YAxis hide domain={["dataMin", "dataMax"]} />
            <Tooltip content={<TipBox />} cursor={{ stroke: "#475569", strokeWidth: 1, strokeDasharray: "2 2" }} />
            <Line
              type="monotone"
              dataKey="equity"
              stroke={color}
              strokeWidth={1.75}
              dot={false}
              isAnimationActive={true}
              animationDuration={900}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>
        {/* Glow at end-of-curve */}
        <div
          className="absolute top-1/2 -translate-y-1/2 right-1 h-1.5 w-1.5 rounded-full"
          style={{ background: color, boxShadow: `0 0 12px 2px ${glow}` }}
        />
      </div>
    </div>
  );
}
